import React from 'react';
import { Shield, Zap } from 'lucide-react';

export type VerificationModeOption = 'off' | 'standard' | 'deep';

interface VerificationToggleProps {
  enabled?: boolean;
  onChange?: (enabled: boolean) => void;
  mode?: VerificationModeOption;
  onModeChange?: (mode: VerificationModeOption) => void;
  disabled?: boolean;
  label?: string;
  helpText?: string;
  showDeepMode?: boolean;
}

const VerificationToggle: React.FC<VerificationToggleProps> = ({
  enabled,
  onChange,
  mode,
  onModeChange,
  disabled = false,
  label = 'Verify with VeriFact-CoT',
  helpText = 'Run independent self-verification on AI output (~30-50% slower, ~30-50% more tokens)',
  showDeepMode = true
}) => {
  const effectiveMode: VerificationModeOption = mode !== undefined
    ? mode
    : (enabled ? 'standard' : 'off');

  const setMode = (next: VerificationModeOption) => {
    if (onModeChange) {
      onModeChange(next);
    }
    if (onChange) {
      onChange(next !== 'off');
    }
  };

  return (
    <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
      <Shield className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={effectiveMode !== 'off'}
            onChange={(e) => setMode(e.target.checked ? 'standard' : 'off')}
            disabled={disabled}
            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm font-medium text-indigo-900">{label}</span>
        </label>
        <p className="text-xs text-indigo-700 mt-0.5 leading-snug">{helpText}</p>

        {showDeepMode && effectiveMode !== 'off' && (
          <div className="mt-2 flex items-center gap-3 pl-6">
            <label className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input
                type="radio"
                name="verification-mode"
                checked={effectiveMode === 'standard'}
                onChange={() => setMode('standard')}
                disabled={disabled}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-indigo-800">Standard</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-xs">
              <input
                type="radio"
                name="verification-mode"
                checked={effectiveMode === 'deep'}
                onChange={() => setMode('deep')}
                disabled={disabled}
                className="text-purple-600 focus:ring-purple-500"
              />
              <span className="text-indigo-800 flex items-center gap-1">
                <Zap className="w-3 h-3 text-purple-600" />
                Deep <span className="text-[10px] text-indigo-500">(applies corrections)</span>
              </span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationToggle;
